<template>
	<div
		ref="wrapperRef"
		class="overflow-hidden relative beforeAfter:content-[''] beforeAfter:transition-opacity"
		:class="[{
				'isolate beforeAfter:absolute beforeAfter:inset-0 beforeAfter:z-10 beforeAfter:w-full beforeAfter:h-full beforeAfter:block beforeAfter:pointer-events-none': isFaded,
				'before:bg-gradient-to-l before:from-transparent before:to-[var(--fade-color,#FFF)] before:from-95%': isFaded && isStartFade,
				'after:bg-gradient-to-l after:from-[var(--fade-color,#FFF)] after:to-transparent after:from-0% after:to-5%': isFaded && isEndFade,
			},
			isFaded && isStartFade ? ' before:opacity-100' : ' before:opacity-0',
			isFaded && isEndFade ? ' after:opacity-100' : ' after:opacity-0',
			wrapperClass
		]"
	>
		<component :is="listTag" ref="listRef" class="w-full min-w-max" v-bind="$attrs">
			<slot />
		</component>
	</div>
</template>

<script lang="ts" setup>
	import {gsap} from 'gsap';
	import {Draggable} from 'gsap/Draggable';
	import {InertiaPlugin} from 'gsap/InertiaPlugin';

	interface Props {
		fadeEdges?: boolean;
		listTag?: string;
		wrapperClass?: string;
	}
	const props = withDefaults(defineProps<Props>(), {listTag: 'ul'});

	defineOptions({inheritAttrs: false});

	const draggable = ref<Draggable>();
	const listRef = ref<HTMLElement>();
	const wrapperRef = ref<HTMLElement>();
	const minX = ref(0);
	const maxX = ref(0);
	const x = ref(0);
	const offset = ref(0);
	const isFaded = ref(false);

	const {width: wrapperWidth} = useElementSize(wrapperRef);
	const {width: listWidth} = useElementSize(listRef);

	const isStartFade = computed(() => x.value < maxX.value - offset.value);
	const isEndFade = computed(() => x.value > minX.value + offset.value);
	const isMounted = ref(false);

	const setupDraggable = () => {
		const listElement = listRef.value;
		if (!isMounted.value || !listElement?.parentElement || !isSliderActive()) {
			return;
		}

		draggable.value = new Draggable(listElement, {
			bounds: listElement.parentElement,
			type: 'x',
			inertia: true,
			throwResistance: 5000,
			edgeResistance: 0.9,
			zIndexBoost: false,
			onMove(this: Draggable) {
				x.value = draggable.value?.x ?? 0;
				minX.value = draggable.value?.minX ?? 0;
			},
		});

		minX.value = draggable.value.minX;

		scrollToActiveTab();
	};

	const scrollToActiveTab = () => {
		if (!draggable.value) {
			return;
		}

		const selectedElement = wrapperRef.value?.querySelector('[aria-selected="true"]') as HTMLElement;
		if (selectedElement && listRef.value) {
			const elementLeft = selectedElement.offsetLeft;
			const elementWidth = selectedElement.offsetWidth;

			const targetX = -(elementLeft - (wrapperWidth.value / 2) + (elementWidth / 2));
			const clampedX = Math.max(Math.min(targetX, maxX.value), minX.value);

			gsap.to(listRef.value, {
				x: clampedX,
				duration: 0.5,
				ease: 'power2.out',
				onUpdate: () => {
					if (draggable.value) {
						draggable.value.update();
						x.value = draggable.value.x;
					}
				},
			});
		}
	};

	const isSliderActive = () => {
		const isActive = listWidth.value > wrapperWidth.value;
		isFaded.value = props.fadeEdges ? isActive : false;
		return isActive;
	};

	const killDraggable = () => {
		draggable.value?.kill();
		listRef.value?.removeAttribute('style');
	};

	useMutationObserver(
		wrapperRef,
		(mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'aria-selected') {
					const target = mutation.target as HTMLElement;
					if (target.getAttribute('aria-selected') === 'true') {
						nextTick(() => {
							scrollToActiveTab();
						});
					}
				}
			});
		},
		{
			attributes: true,
			attributeFilter: ['aria-selected'],
			subtree: true,
		},
	);

	onMounted(async () => {
		isMounted.value = true;
		gsap.registerPlugin(Draggable, InertiaPlugin);
		await nextTick();
		setupDraggable();
	});

	onBeforeUnmount(() => {
		isMounted.value = false;
		killDraggable();
	});

	watchDebounced(
		[wrapperWidth, listWidth],
		() => {
			if (!isMounted.value) {
				return;
			}
			killDraggable();
			setupDraggable();
		},
		{debounce: 300},
	);

	defineExpose({scrollToActiveTab});
</script>

<script lang="ts">
	export default {inheritAttrs: false};
</script>
